import { create } from "zustand";
import { fetchMe } from "@/api/auth";
import { queryClient } from "@/lib/queryClient";
import type { MicrositeUser } from "@/types/api";

/**
 * Microsite auth store. Access token lives in memory only — refresh
 * token rides in the httpOnly `microsite_rt` cookie. On app boot we
 * call /auth/refresh and either rehydrate the access token or land on
 * the public state.
 */
interface AuthState {
  accessToken: string | null;
  user: MicrositeUser | null;
  bootStatus: "idle" | "loading" | "ready";

  setAuth: (accessToken: string, user: MicrositeUser) => void;
  setUser: (user: MicrositeUser) => void;
  refreshUser: () => Promise<void>;
  clearAuth: () => void;
  setBootStatus: (status: "idle" | "loading" | "ready") => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  user: null,
  bootStatus: "idle",

  setAuth: (accessToken, user) => {
    // Wipe the query cache when the authenticated identity actually changes
    // (login as a different PIC, account switch). Query keys are global, so a
    // stale entry would otherwise leak the previous user's commitment/runner
    // data into the new one. Benign same-user token refreshes (which also call
    // setAuth) keep the cache — id is unchanged.
    const prevUser = get().user;
    if (prevUser && prevUser.id !== user.id) {
      queryClient.clear();
    }
    set({ accessToken, user, bootStatus: "ready" });
  },
  setUser: (user) => set({ user }),
  refreshUser: async () => {
    try {
      const user = await fetchMe();
      set({ user });
    } catch {
      // Silent — the next /auth/refresh or page load will heal it.
    }
  },
  clearAuth: () => {
    // Drop all cached data on logout / refresh failure so the next user on this
    // browser starts clean.
    queryClient.clear();
    set({ accessToken: null, user: null, bootStatus: "ready" });
  },
  setBootStatus: (bootStatus) => set({ bootStatus })
}));
